import { takeLatest, takeEvery, put, delay, select, call, spawn } from 'redux-saga/effects';
import copy from 'copy-to-clipboard';
import { getGeneralInformation, getExpressConfiguration, getUsers, getSearchText } from '../selectors/userSelectors';
import { enqueueSnackbar } from '../../app/actions/appActions';
import {
    CREATE_USER_REQUEST,
    GET_USERS_REQUEST,
    DELETE_USER_REQUEST,
    GET_TELEPHONY_PROFILES_REQUEST,
    GET_USER_REQUEST,
    UPDATE_USER_REQUEST,
    COPY_IP
} from '../actions/userActionTypes';
import {
    createUserSuccess,
    createUserFailure,
    getUsersSuccess,
    getUsersFailure,
    setTotalUsers,
    deleteUserSuccess,
    deleteUserFailure,
    getTelephonySuccess,
    getTelephonyFailure,
    getUserSuccess,
    getUserFailure,
    updateUserSuccess,
    updateUserFailure
} from '../actions/userActions';
import {
    createUserService,
    deleteUserService,
    getUsersService,
    getTelephonyProfilesService,
    getUserByIDService,
    updateUserService,
    uploadUserPhoto
} from '../services/usersServices';
import { buildUser } from '../helpers/buildUsers';

const createUserTask = function* (action) {
    try {
        const { history } = action.payload;

        const generalInformation = yield select(getGeneralInformation);
        const expressConfiguration = yield select(getExpressConfiguration);

        const user = buildUser(generalInformation, expressConfiguration);

        const { data: { user: { id } } } = yield call(createUserService, user);

        if (user.Picture) {
            yield spawn(uploadUserPhoto, user.Picture, id);
        }

        yield put(createUserSuccess());
        yield put(enqueueSnackbar({
            message: 'users:user_created',
            options: { variant: 'success' }
        }));

        history.push('/users');
    } catch (error) {
        yield put(createUserFailure());
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

const getUsersTask = function* (action) {
    try {
        const { total, offset, searchText } = action.payload;

        if (searchText) {
            yield delay(500);
        }

        const { data } = yield call(getUsersService, total, offset, 50, searchText);

        if (total) {
            yield put(setTotalUsers(data.Total));
            yield put(getUsersSuccess(data.Users));
        } else {
            yield put(getUsersSuccess(data));
        }
    } catch (error) {
        yield put(getUsersFailure());
    }
};

const deleteUserTask = function* (action) {
    try {
        const { id } = action.payload;

        yield call(deleteUserService, id);
        yield put(deleteUserSuccess(id));

        const visibleUsers = yield select(getUsers);
        const searchText = yield select(getSearchText);
        const { data } = yield call(getUsersService, 0, visibleUsers.length, 1, searchText);

        yield put(getUsersSuccess(data));
        yield put(enqueueSnackbar({
            message: 'users:user_deleted',
            options: { variant: 'success' }
        }));
    } catch (error) {
        yield put(deleteUserFailure(action.payload.id));
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

const getTelephonyRequestTask = function* () {
    try {
        const { data: telephonyProfiles } = yield call(getTelephonyProfilesService);

        yield put(getTelephonySuccess(telephonyProfiles));
    } catch (error) {
        yield put(getTelephonyFailure());
    }
};

const getUserTask = function* (action) {
    try {
        const { id } = action.payload;
        const { data: user } = yield call(getUserByIDService, id);

        yield put(getUserSuccess(user[0]));
    } catch (error) {
        yield put(getUserFailure());
    }
};

const updateUserTask = function* (action) {
    try {
        const { id, history } = action.payload;

        const generalInformation = yield select(getGeneralInformation);
        const expressConfiguration = yield select(getExpressConfiguration);

        const user = buildUser(generalInformation, expressConfiguration);

        yield call(updateUserService, user, id);

        if (user.Picture) {
            yield call(uploadUserPhoto, user.Picture, id);
        }

        yield put(updateUserSuccess());
        yield put(enqueueSnackbar({
            message: 'users:user_updated',
            options: { variant: 'success' }
        }));

        history.push('/users');
    } catch (error) {
        yield put(updateUserFailure());
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

const copyIPTask = function* (action) {
    try {
        const { message, ip } = action.payload;

        copy(ip);

        yield put(enqueueSnackbar({
            message: message,
            options: { variant: 'success' }
        }));
    } catch (error) {
        yield put(enqueueSnackbar({
            message: 'validations:ERROR',
            options: { variant: 'error' }
        }));
    }
};

export default function* usersSaga() {
    yield takeLatest(CREATE_USER_REQUEST, createUserTask);
    yield takeLatest(GET_USERS_REQUEST, getUsersTask);
    yield takeEvery(DELETE_USER_REQUEST, deleteUserTask);
    yield takeLatest(GET_TELEPHONY_PROFILES_REQUEST, getTelephonyRequestTask);
    yield takeLatest(GET_USER_REQUEST, getUserTask);
    yield takeLatest(UPDATE_USER_REQUEST, updateUserTask);
    yield takeLatest(COPY_IP, copyIPTask);
};
